import React, { useEffect, useState } from "react";
import { CustomizedTreeItem } from "./CustomizedComponents";
import RenderLabelWithIcon from "./RenderLabelWithIcon";
import PropTypes from "prop-types";
import {
	clearCurrentDeviceDragPayload,
	createDeviceDragPayload,
	getDeviceGroupDropDataset,
	isPlaceableDeviceEntity,
	populateDeviceDragEvent,
	setCurrentDeviceDragPayload
} from "../deviceTreeDnd";

const RecursiveTreeView = ({
	nodes,
	expandedNodes,
	addOrUpdateNodeLabel,
	addNewNode,
	selectedNodeForEdit,
	setSelectedNodeForEdit,
	onContextMenu,
	addToDeviceGroup,
	readOnly,
	getSelectedNodes,
	isForFacilitiesFloorPlan,
	getSelectedDevice,
	displayChevrons,
	getSelectedMenuItem,
	allowDeviceSelection,
	parentCount,
	onDeviceDropToGroup,
	activeDropGroupId,
	onDropTargetHoverChange,
	onChevronToggle,
	enableDevicePlacementDrag,
	enableDeviceGroupDrag
}) => {
	const [hoveredNodeId, setHoveredNodeId] = useState(null);
	const [selectedDevice, setSelectedDevice] = useState("");

	useEffect(() => {
		getSelectedDevice(selectedDevice);
	}, [getSelectedDevice, selectedDevice]);

	return (
		<>
			{nodes.map(node => {
				const childParentCount = parentCount + (node?.id === "root" ? 0 : 1);
				const isGroupDropTargetHighlighted = !!(
					node?.isGroup &&
					node?.id !== "root" &&
					node?.id === activeDropGroupId
				);
				const isDraggableDevice =
					(enableDevicePlacementDrag || enableDeviceGroupDrag) && !readOnly && isPlaceableDeviceEntity(node);

				const dragPayload = isDraggableDevice
					? createDeviceDragPayload({
							source: "tree",
							entityId: node?.id,
							entityType: node?.entityType,
							sourceGroupId: node?.parentGroupId,
							name: node?.name,
							feedId: node?.feedId,
							entityData: node?.entityData,
							icon: node?.icon,
							deviceStateColor: node?.deviceStateColor,
							deviceType: node?.deviceType
						})
					: null;

				const handleTreeItemDragStart = event => {
					if (!dragPayload) {
						event.preventDefault();
						return;
					}

					populateDeviceDragEvent(event, dragPayload);
					setCurrentDeviceDragPayload(dragPayload);
				};

				const handleTreeItemDragEnd = () => {
					clearCurrentDeviceDragPayload();
				};

				return (
					<CustomizedTreeItem
						key={node?.id}
						nodeId={node?.id}
						draggable={false}
						label={
							<RenderLabelWithIcon
								node={node}
								isExpanded={expandedNodes.includes(node?.id)}
								hoveredNodeId={hoveredNodeId}
								setHoveredNodeId={setHoveredNodeId}
								selectedNodeForEdit={selectedNodeForEdit}
								setSelectedNodeForEdit={setSelectedNodeForEdit}
								addOrUpdateNodeLabel={addOrUpdateNodeLabel}
								addNewNode={addNewNode}
								readOnly={readOnly}
								addToDeviceGroup={addToDeviceGroup}
								getSelectedNodes={getSelectedNodes}
								isForFacilitiesFloorPlan={isForFacilitiesFloorPlan}
								setSelectedDevice={setSelectedDevice}
								selectedDevice={selectedDevice}
								getSelectedDevice={getSelectedDevice}
								displayChevrons={displayChevrons}
								getSelectedMenuItem={getSelectedMenuItem}
								allowDeviceSelection={allowDeviceSelection}
								parentCount={parentCount}
								onDeviceDragStart={isDraggableDevice ? handleTreeItemDragStart : undefined}
								onDeviceDragEnd={isDraggableDevice ? handleTreeItemDragEnd : undefined}
								isTreeItemDraggable={isDraggableDevice}
								onDeviceDropToGroup={onDeviceDropToGroup}
								onDropTargetHoverChange={onDropTargetHoverChange}
								onChevronToggle={onChevronToggle}
								enableDevicePlacementDrag={enableDevicePlacementDrag}
								enableDeviceGroupDrag={enableDeviceGroupDrag} data-testid="orion-components-device-manager-device-tree-components-recursive-tree-view-render-label-with-icon"
							/>
						}
						expandIcon={null}
						collapseIcon={null}
						sx={{
							outline: "none",
							border: "none",
							width: "100%",
							...(isGroupDropTargetHighlighted
								? {
										outline: "1px dashed #35B7F3",
										backgroundColor: "rgba(53, 183, 243, 0.14)",
										boxShadow: "0 0 0 1px rgba(53, 183, 243, 0.35) inset",
										borderRadius: "4px"
									}
								: {}),
							"& .MuiTreeItem-content": { width: "calc(100% - 40px) !important", minWidth: 0 },
							"& .MuiTreeItem-content.Mui-selected": {
								width: "calc(100% - 40px) !important"
							},
							"& .MuiTreeItem-label": {
								display: "block",
								width: "calc(100% - 40px) !important",
								minWidth: 0
							}
						}}
						onContextMenu={e => {
							e.preventDefault();
							e.stopPropagation();
							onContextMenu(e, { ...node, parentCount }); // Pass the correct node
						}}
						{...getDeviceGroupDropDataset(node)} data-testid="orion-components-device-manager-device-tree-components-recursive-tree-view-customized-tree-item"
					>
						{node?.children?.length > 0 && (
							<RecursiveTreeView
								nodes={node?.children}
								expandedNodes={expandedNodes}
								addOrUpdateNodeLabel={addOrUpdateNodeLabel}
								addNewNode={addNewNode}
								selectedNodeForEdit={selectedNodeForEdit}
								setSelectedNodeForEdit={setSelectedNodeForEdit}
								onContextMenu={onContextMenu}
								addToDeviceGroup={addToDeviceGroup}
								readOnly={readOnly}
								getSelectedNodes={getSelectedNodes}
								isForFacilitiesFloorPlan={isForFacilitiesFloorPlan}
								getSelectedDevice={getSelectedDevice}
								displayChevrons={displayChevrons}
								getSelectedMenuItem={getSelectedMenuItem}
								allowDeviceSelection={allowDeviceSelection}
								parentCount={childParentCount}
								onDeviceDropToGroup={onDeviceDropToGroup}
								activeDropGroupId={activeDropGroupId}
								onDropTargetHoverChange={onDropTargetHoverChange}
								onChevronToggle={onChevronToggle}
								enableDevicePlacementDrag={enableDevicePlacementDrag}
								enableDeviceGroupDrag={enableDeviceGroupDrag} data-testid="orion-components-device-manager-device-tree-components-recursive-tree-view-recursive-tree-view"
							/>
						)}
					</CustomizedTreeItem>
				);
			})}
		</>
	);
};

RecursiveTreeView.propTypes = {
	nodes: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			name: PropTypes.string,
			isDevice: PropTypes.bool,
			isGroup: PropTypes.bool,
			entityType: PropTypes.string,
			children: PropTypes.array
		})
	).isRequired,
	expandedNodes: PropTypes.arrayOf(PropTypes.string).isRequired,
	addOrUpdateNodeLabel: PropTypes.func,
	addNewNode: PropTypes.func,
	selectedNodeForEdit: PropTypes.string,
	setSelectedNodeForEdit: PropTypes.func,
	onContextMenu: PropTypes.func,
	addToDeviceGroup: PropTypes.bool,
	readOnly: PropTypes.bool,
	getSelectedNodes: PropTypes.func,
	isForFacilitiesFloorPlan: PropTypes.bool,
	getSelectedDevice: PropTypes.func,
	displayChevrons: PropTypes.bool,
	parentCount: PropTypes.number,
	onDeviceDropToGroup: PropTypes.func,
	activeDropGroupId: PropTypes.string,
	onDropTargetHoverChange: PropTypes.func,
	onChevronToggle: PropTypes.func,
	enableDevicePlacementDrag: PropTypes.bool,
	enableDeviceGroupDrag: PropTypes.bool
};

RecursiveTreeView.defaultProps = {
	nodes: [],
	expandedNodes: [],
	selectedNodeForEdit: null,
	addToDeviceGroup: false,
	readOnly: false,
	isForFacilitiesFloorPlan: false,
	displayChevrons: true,
	parentCount: 0,
	enableDevicePlacementDrag: false,
	enableDeviceGroupDrag: false
};

export default RecursiveTreeView;
